/**
 * The index of what changed — a navigator for the diff document beside it.
 *
 * Deliberately thin. It used to repeat each section's structural notes and word
 * counts, but the document now carries its own headers and its own notes, so
 * anything more than a title and a delta here is the same fact printed twice.
 */

import { Box, CircularProgress, Typography } from '@mui/material'
import type { ChangeMapResponse } from '@shared/contentHistory'
import type { SegmentChange } from '@shared/diff/types'
import React from 'react'

interface ChangeIndexProps {
  changes: ChangeMapResponse | null
  loading: boolean
  selectedKey: string | null
  onSelect: (entry: SegmentChange) => void
  direction: 'ltr' | 'rtl'
}

/** Only the kinds that are not plain edits are worth a word here. */
const KIND_LABELS: Partial<Record<SegmentChange['kind'], string>> = {
  added: 'new',
  removed: 'deleted',
  moved: 'moved',
  renamed: 'retitled',
  unchanged: 'structure',
}

const KIND_COLORS: Record<SegmentChange['kind'], string> = {
  added: 'var(--diff-insert-border)',
  removed: 'var(--diff-delete-border)',
  edited: 'var(--diff-changed-bar)',
  moved: 'var(--diff-moved-border)',
  renamed: 'var(--diff-moved-border)',
  unchanged: 'var(--border-primary)',
}

export default function ChangeIndex({ changes, loading, selectedKey, onSelect, direction }: ChangeIndexProps) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={20} />
      </Box>
    )
  }

  if (!changes || changes.files.length === 0) {
    return (
      <Typography sx={{ p: 2, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
        {changes ? 'Nothing changed in this range.' : 'Pick a point in the timeline.'}
      </Typography>
    )
  }

  return (
    <Box sx={{ px: 1, py: 1 }}>
      {/* One card per source file. With a long range the index runs to dozens of
          rows, and a border is what tells you where one file's changes end and
          the next begin — spacing alone stops reading as a boundary. */}
      {changes.files.map((group) => (
        <Box
          key={group.filename}
          sx={{
            mb: 1,
            border: '1px solid var(--border-subtle)',
            borderRadius: 1,
            overflow: 'hidden',
            // The card sits on its own surface, a step off the pane behind it.
            // Applied to the whole rectangle rather than to the title strip, so
            // it reads as one raised group instead of a highlighted row.
            bgcolor: 'var(--bg-surface-alt)',
          }}
        >
          {/* Source file. Not uppercased or letter-spaced: both are Latin
              typographic habits — uppercase does nothing in Hebrew and tracking
              actively hurts it. */}
          <Typography
            dir={direction}
            sx={{
              px: 1,
              py: 0.5,
              fontSize: '0.8rem',
              fontWeight: 700,
              color: 'var(--text-secondary)',
              // No fill: a filled background already means "selected row" here,
              // and using it for a group header too made the two read alike.
              // The card border groups; the rule separates; weight does the rest.
              borderBottom: '1px solid var(--border-subtle)',
            }}
          >
            {group.displayTitle}
          </Typography>

          <Box sx={{ p: 0.5 }}>
            {group.entries.map((entry) => {
              const selected = entry.key === selectedKey
              const label = KIND_LABELS[entry.kind]
              return (
                <Box
                  key={entry.key}
                  onClick={() => onSelect(entry)}
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 0.75,
                    px: 0.75,
                    py: 0.4,
                    cursor: 'pointer',
                    borderRadius: 1,
                    bgcolor: selected ? 'var(--sidebar-selected)' : 'transparent',
                    [direction === 'rtl' ? 'borderRight' : 'borderLeft']: `2px solid ${KIND_COLORS[entry.kind]}`,
                    '&:hover': { bgcolor: selected ? 'var(--sidebar-selected)' : 'var(--bg-hover)' },
                  }}
                >
                  {/* A `^` section is a reader-visible unit; everything else is
                    working material. The marker carries that distinction, so the
                    type only has to support it — one step of weight and one of
                    colour. Varying the size too made the list look broken rather
                    than ordered, and left the rows at uneven heights. Read off the
                    `^` flag, never off bracket conventions in the title. */}
                  <Typography
                    dir={direction}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: '0.82rem',
                      fontWeight: entry.isChapterSection ? 600 : 400,
                      color: selected
                        ? 'var(--sidebar-selected-text)'
                        : entry.isChapterSection
                          ? 'var(--text-primary)'
                          : 'var(--text-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '0.9em',
                        fontWeight: 700,
                        color: 'var(--text-tertiary)',
                      }}
                    >
                      {entry.isChapterSection ? '^' : ''}
                    </Box>
                    {entry.displayTitle}
                    {label && (
                      <Box component="span" sx={{ color: 'var(--text-tertiary)', fontSize: '0.66rem' }}>
                        {' '}
                        · {label}
                      </Box>
                    )}
                  </Typography>

                  {(entry.wordsAdded > 0 || entry.wordsRemoved > 0) && (
                    // `dir="ltr"` with isolation: `+` and `−` are bidi-neutral, so
                    // inside Hebrew text they attach to the wrong side and the pair
                    // renders mirrored — "+1 −163" came out as "163− 1+".
                    <Typography
                      dir="ltr"
                      sx={{
                        flexShrink: 0,
                        fontSize: '0.66rem',
                        fontFamily: 'monospace',
                        unicodeBidi: 'isolate',
                      }}
                    >
                      <Box component="span" sx={{ color: 'var(--diff-insert-border)' }}>
                        +{entry.wordsAdded}
                      </Box>{' '}
                      <Box component="span" sx={{ color: 'var(--diff-delete-border)' }}>
                        −{entry.wordsRemoved}
                      </Box>
                    </Typography>
                  )}
                </Box>
              )
            })}
          </Box>
        </Box>
      ))}
    </Box>
  )
}
